// Pure helpers for the Today screen (plan todo 20). Alias-free on purpose so
// the node vitest project can import this module directly without mocking
// "@/" specifiers — every interactive behavior on the screen funnels through
// one of these functions so renderToStaticMarkup suites can assert contracts.

export const MEAL_GROUP_ORDER = [
  "breakfast",
  "lunch",
  "dinner",
  "snack",
  "uncategorized",
] as const;

export type MealGroupKey = (typeof MEAL_GROUP_ORDER)[number];

export const MEAL_GROUP_LABELS: Record<MealGroupKey, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
  uncategorized: "Uncategorized",
};

export interface MealGroup<T> {
  readonly key: MealGroupKey;
  readonly label: string;
  readonly entries: readonly T[];
}

// Legacy entries predate mealType and land in the trailing "Uncategorized"
// bucket; anything unrecognized defensively joins them there too.
export function mealGroupKeyFor(mealType: string | undefined): MealGroupKey {
  switch (mealType) {
    case "breakfast":
    case "lunch":
    case "dinner":
    case "snack":
      return mealType;
    default:
      return "uncategorized";
  }
}

// Groups render in canonical order and empty groups are hidden entirely —
// an all-empty day keeps the screen's existing empty state instead.
export function groupEntriesByMealType<T extends { mealType?: string }>(
  entries: readonly T[],
): MealGroup<T>[] {
  const buckets = new Map<MealGroupKey, T[]>();
  for (const entry of entries) {
    const key = mealGroupKeyFor(entry.mealType);
    const bucket = buckets.get(key);
    if (bucket === undefined) {
      buckets.set(key, [entry]);
    } else {
      bucket.push(entry);
    }
  }
  const groups: MealGroup<T>[] = [];
  for (const key of MEAL_GROUP_ORDER) {
    const bucket = buckets.get(key);
    if (bucket !== undefined && bucket.length > 0) {
      groups.push({ key, label: MEAL_GROUP_LABELS[key], entries: bucket });
    }
  }
  return groups;
}

// Entry action sheet contract (plan todo 20): exactly these six actions, in
// this order. Move targets never include "uncategorized" — the picker offers
// the four canonical meal types only.
export type EntryActionKey =
  | "edit"
  | "duplicate"
  | "move"
  | "saveLibrary"
  | "saveMemory"
  | "delete";

export interface EntryActionDescriptor {
  readonly key: EntryActionKey;
  readonly label: string;
  readonly testId: string;
}

export const ENTRY_ACTIONS: readonly EntryActionDescriptor[] = [
  { key: "edit", label: "Edit", testId: "entry-action-edit" },
  { key: "duplicate", label: "Duplicate", testId: "entry-action-duplicate" },
  { key: "move", label: "Move to another meal", testId: "entry-action-move" },
  { key: "saveLibrary", label: "Save to library", testId: "entry-action-save-library" },
  { key: "saveMemory", label: "Save as memory", testId: "entry-action-save-memory" },
  { key: "delete", label: "Delete", testId: "entry-action-delete" },
];

export const MOVE_TARGET_TYPES = ["breakfast", "lunch", "dinner", "snack"] as const;
export type MoveTargetType = (typeof MOVE_TARGET_TYPES)[number];

export interface EntryKcalSource {
  readonly finalKcal?: number;
  readonly estimatedKcal?: number;
  readonly kcalUnknown?: boolean;
}

// Unknown calories stay unknown: undefined when the entry is flagged
// kcalUnknown or carries no resolved value. A measured 0 stays 0 — the
// display layer renders "—" only for undefined, never a fabricated number.
export function entryKcal(entry: EntryKcalSource): number | undefined {
  if (entry.kcalUnknown === true) return undefined;
  return entry.finalKcal ?? entry.estimatedKcal;
}

export function formatQuantityUnit(
  quantity: number | undefined,
  unit: string | undefined,
): string | null {
  const trimmedUnit = unit?.trim() ?? "";
  if (quantity === undefined && trimmedUnit.length === 0) return null;
  const parts: string[] = [];
  if (quantity !== undefined) parts.push(String(quantity));
  if (trimmedUnit.length > 0) parts.push(trimmedUnit);
  return parts.join(" ");
}

interface LogStatusLike {
  readonly status?: string;
}

// Copy-yesterday visibility (plan todo 20): only when yesterday had logged
// entries AND the selected day has none. Planned/approved-but-unlogged rows
// do not count on either side.
export function countLoggedEntries<T extends LogStatusLike>(entries: readonly T[]): number {
  return entries.filter((entry) => entry.status === "logged").length;
}

export function canCopyYesterday(args: {
  readonly yesterdayLoggedCount: number;
  readonly todayLoggedCount: number;
}): boolean {
  return args.yesterdayLoggedCount > 0 && args.todayLoggedCount === 0;
}

export function unknownKcalNotice(count: number): string {
  const noun = count === 1 ? "item" : "items";
  return `${count} ${noun} with unknown calories — totals are partial`;
}
